import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useTaskStore } from './taskStore';
import { modalScrollLock } from '../utils/modalScrollLock';

/**
 * UI State Store
 * 
 * Manages UI-related state like sidebar visibility, modals, and dark mode.
 * 
 * NOTE: Notification badge state is now managed via NotificationStateContext
 * (useNotificationState/useSidebarBadges/useBellUnreadCount hooks).
 * The badge state is stored in the database using the UserNotificationState model,
 * ensuring cross-device consistency and preventing race conditions.
 */
interface UIState {
  sidebarOpen: boolean;
  darkMode: boolean;
  taskModalOpen: boolean;
  editingTaskId: string | null;
  isRecurringTask: boolean;
  projectModalOpen: boolean;
  editingProjectId: string | null;
  mobileMenuOpen: boolean;
  // Files shared from the OS PWA share target, consumed by TaskForm
  sharedFiles: File[];
  setSharedFiles: (files: File[]) => void;
  clearSharedFiles: () => void;
  
  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  
  // Theme actions
  toggleDarkMode: () => void;
  
  // Task modal actions
  openTaskModal: (taskId?: string, options?: { isRecurring?: boolean }) => void;
  closeTaskModal: () => void;
  
  // Project modal actions
  openProjectModal: (projectId?: string) => void;
  closeProjectModal: () => void;
  
  // Mobile menu actions
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      darkMode: false,
      taskModalOpen: false,
      editingTaskId: null,
      isRecurringTask: false,
      projectModalOpen: false,
      editingProjectId: null,
      mobileMenuOpen: false,
      sharedFiles: [],

      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleDarkMode: () => {
        const newMode = !get().darkMode;
        set({ darkMode: newMode });
        if (newMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      openTaskModal: (taskId, options) => {
        // When opening modal for a NEW task (no taskId), clear any previous currentTask
        // to ensure the form starts clean
        if (!taskId) {
          useTaskStore.getState().clearCurrentTask();
        }
        set({ 
          taskModalOpen: true, 
          editingTaskId: taskId || null,
          isRecurringTask: options?.isRecurring ?? false,
        });
      },
      closeTaskModal: () => {
        // Clear currentTask from taskStore to ensure clean state when opening new task modal
        useTaskStore.getState().clearCurrentTask();
        set({ taskModalOpen: false, editingTaskId: null, isRecurringTask: false });
        // FIX: Force-reset scroll lock to guarantee body styles (position:fixed,
        // overflow:hidden, etc.) are removed. This is a safety net against
        // modalScrollLock counter desync (e.g., React.StrictMode double-mount in
        // dev, or multiple components sharing the singleton). Without this, on
        // mobile the body could stay locked → hamburger unreachable → user
        // cannot reopen sidebar to create new tasks without restarting the app.
        modalScrollLock.reset();
      },

      openProjectModal: (projectId) => set({ projectModalOpen: true, editingProjectId: projectId || null }),
      closeProjectModal: () => set({ projectModalOpen: false, editingProjectId: null }),

      toggleMobileMenu: () => set({ mobileMenuOpen: !get().mobileMenuOpen }),
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
      setSharedFiles: (files) => set({ sharedFiles: files }),
      clearSharedFiles: () => set({ sharedFiles: [] }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({ darkMode: state.darkMode }),
    }
  )
);

// Initialize dark mode on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('ui-storage');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.darkMode) {
        document.documentElement.classList.add('dark');
      }
    } catch {
      // Ignore parse errors
    }
  }
}